import {
    DEFAULT_REF_PARAMS,
    NON_PERSISTABLE_URL_PARAM_VALUES,
} from 'App/constants';

import { getStorageValue, setStorageValue } from './localStorageHandler';

/**
 * @param {*} value Merged URL-or-default value before storage reconciliation.
 * @returns {boolean}
 */

const shouldPersistUrlParam = (value) =>
    !NON_PERSISTABLE_URL_PARAM_VALUES.has(value);

/**
 * @param {string} key Param name (for example, `refid1`, `hash`, …).
 * @param {string|undefined} nestedStorageKey JSON blob key, or `undefined` for flat `localStorage`.
 * @returns {string|undefined|null} Prior value if any.
 */

function readUrlParamFromStorage(key, nestedStorageKey) {
    if (nestedStorageKey === undefined) {
        return localStorage.getItem(key);
    }

    return getStorageValue(nestedStorageKey, `app.${key}`);
}

/**
 * @param {string} key
 * @param {string|undefined} nestedStorageKey
 * @param {string} value
 */

function writeUrlParamToStorage(key, nestedStorageKey, value) {
    if (nestedStorageKey === undefined) {
        localStorage.setItem(key, value);
        return;
    }

    setStorageValue(nestedStorageKey, `app.${key}`, value);
}

/**
 * @returns {URLSearchParams} Parsed query string from `window.location.search` (excludes hash).
 */

const getLocationSearchParams = () =>
    new URLSearchParams(window.location.search);

/**
 * Parses params carried inside the fragment, which happens when a link puts the
 * phase before the query (`/#2?rid=147&refid1=test`). Everything after the first
 * `?` in `window.location.hash` is treated as the query.
 *
 * @returns {URLSearchParams} Empty when the fragment holds no query part.
 */

const getLocationHashParams = () => {
    const [, ...hashQuery] = window.location.hash.split('?');

    return new URLSearchParams(hashQuery.join('?'));
};

/**
 * Merges the query string with fragment params. `window.location.search` is
 * authoritative; fragment params only fill in keys the query string omits, so
 * standard `?param` URLs resolve exactly as before.
 *
 * @returns {URLSearchParams}
 */

const getUrlParams = () => {
    const params = getLocationSearchParams();

    for (const [key, value] of getLocationHashParams()) {
        if (!params.has(key)) params.append(key, value);
    }

    return params;
};

/**
 * Builds refid/hash/rid values from the current page query string, merging URL,
 * per-key defaults, and values previously saved under `nestedStorageKey`.
 *
 * Params are read from `window.location.search` first and from the fragment query
 * second (`/#2?rid=147&refid1=test`), so phase-prefixed links keep their params.
 *
 * Resolution per key: URL wins when present; otherwise the configured default.
 * For `rid` only, `rid=` (empty value) is treated as absent so `options.rid` applies.
 * If the merged value is a placeholder (`'0'`, `null`, `undefined`, or `''`), a
 * stored value at `app.<key>` (or flat `localStorage` when `nestedStorageKey` is omitted)
 * may replace it. Non-placeholder values are written back to storage, except `rid`
 * is never written to flat `localStorage` when `nestedStorageKey` is omitted.
 *
 * @param {object} [options={}] May be omitted (`getUrlParamObj()`), or an object with
 *   any combination of `nestedStorageKey` and `rid` (only `nestedStorageKey`, only `rid`, or both).
 * @param {string|undefined} [options.nestedStorageKey] Namespace key for `getStorageValue` /
 *   `setStorageValue`. When omitted, ref params use flat `localStorage` keys.
 * @param {string|null|undefined} [options.rid=null] Default for `rid` when the URL has
 *   no `rid` param or only `rid=` (empty).
 * @returns {{
 *   refid1: string,
 *   refid2: string,
 *   refid3: string,
 *   hash: string|undefined,
 *   rid: string|null
 * }}
 */

export const getUrlParamObj = ({ nestedStorageKey, rid = null } = {}) => {
    const defaultParams = {
        ...DEFAULT_REF_PARAMS,
        hash: undefined,
        rid,
    };
    const params = getUrlParams();
    const result = {};

    for (const [key, defaultValue] of Object.entries(defaultParams)) {
        let fromUrl = params.get(key);
        // `?rid=` yields `''`; treat as missing so a caller-provided default `rid` wins.
        if (key === 'rid' && fromUrl === '') fromUrl = null;

        let resolved = fromUrl !== null ? fromUrl : defaultValue;

        if (shouldPersistUrlParam(resolved)) {
            // When using flat `localStorage` (`nestedStorageKey` omitted), `rid` must not be written.
            if (nestedStorageKey !== undefined || key !== 'rid') {
                writeUrlParamToStorage(key, nestedStorageKey, resolved);
            }
        } else {
            const stored = readUrlParamFromStorage(key, nestedStorageKey);

            if (stored) {
                resolved = stored;
            }
        }

        result[key] = resolved;
    }

    return result;
};

/**
 * Reads any name single query parameter from `window.location.search`, falling
 * back to the fragment query (`/#2?build=1.2.3`) when the key is not in the
 * query string.
 *
 * @param {string} inputKey Parameter name.
 * @returns {string|null} The raw value, or `null` when missing or empty string.
 */

export const getUrlParamValue = (inputKey) => {
    const urlParams = getUrlParams();

    return urlParams.get(inputKey) || null;
};

/**
 * Replaces `{refid1}` placeholders in a fallback URL with values from the
 * current query string and localStorage.
 *
 * @param {string} template Fallback URL from LP config.
 * @param {string} refid1 Refid1 value.
 * @returns {string}
 */

export const resolveFallbackUrl = (template, refid1) => {
    if (!template?.includes('{refid1}')) {
        return template;
    }

    return template.replace(/\{refid1}/g, refid1);
};

/**
 * Returns the leading phase fragment from `window.location.hash` (without `#`),
 * trimmed. When the fragment looks like `phase?ref…`, only the `phase` segment is
 * returned. This is unrelated to the `?hash=` lead-id query parameter.
 *
 * @returns {string|null} The hash segment, or `null` when absent, blank, or `'0'`.
 */

export const getPhaseFromHash = () => {
    const hash = window.location.hash.substring(1).trim();

    if (!hash || hash === '0') return null;

    return hash.includes('?') ? hash.split('?')[0] : hash;
};

/**
 * Updates `window.location.hash` in place when the current fragment holds a numeric
 * phase: `#N` becomes `#value`, and a `#N?params` form keeps `params` untouched. No-op
 * if there is no fragment after `#` or the phase segment is not numeric; phase
 * fragments are opt-in developer/deep-link behavior and must not be added to ordinary
 * navigation. This is unrelated to the `?hash=` lead-id query parameter.
 *
 * @param {string} value New numeric phase string.
 * @returns {void}
 */

export const setPhaseHash = (value) => {
    const urlHash = window.location.hash.substring(1);

    if (!urlHash) return;

    const [phase, ...hashQuery] = urlHash.split('?');

    if (isNaN(+phase)) return;

    const query = hashQuery.join('?');

    window.location.hash = query ? `${value}?${query}` : value;
};
